<template>
    <div class="content mk-report">
        <base-header class="pb-6">
            <div class="row align-items-center py-4">
                <div class="col-lg-6 col-7">
                    <h6 class="h2 text-white d-inline-block mb-0">{{ $t("marketing.report.title") }}</h6>
                    <nav aria-label="breadcrumb" class="d-none d-md-inline-block ml-md-4">
                        <route-breadcrumb></route-breadcrumb>
                    </nav>
                </div>
            </div>
        </base-header>

        <div class="container-fluid mt--6">
            <!-- filter bar -->
            <card class="mb-3">
                <div class="row">
                    <div class="col-xl-4 col-md-6 form-group">
                        <label class="form-control-label">{{ $t("marketing.report.range") }}</label>
                        <el-date-picker v-model="f.range" type="daterange" value-format="yyyy-MM-dd" format="yyyy-MM-dd" class="w-100" :clearable="false" unlink-panels data-testid="rep-range"></el-date-picker>
                    </div>
                    <div class="col-xl-2 col-md-3 form-group">
                        <label class="form-control-label">{{ $t("marketing.report.model") }}</label>
                        <el-select v-model="f.model" class="w-100">
                            <el-option value="last" :label="$t('marketing.report.model_last')"></el-option>
                            <el-option value="first" :label="$t('marketing.report.model_first')"></el-option>
                        </el-select>
                    </div>
                    <div class="col-xl-3 col-md-3 form-group">
                        <label class="form-control-label">{{ $t("marketing.report.group_by") }}</label>
                        <el-select v-model="f.group_by" class="w-100" data-testid="rep-group">
                            <el-option v-for="g in groups" :key="g" :value="g" :label="$t('marketing.report.group.' + g)"></el-option>
                        </el-select>
                    </div>
                    <div class="col-xl-3 col-md-6 form-group">
                        <label class="form-control-label">{{ $t("marketing.report.channel") }}</label>
                        <el-select v-model="f.source_channel" class="w-100" clearable :placeholder="$t('marketing.report.channel_all')">
                            <el-option v-for="c in channels" :key="c" :value="c" :label="$t('marketing.report.channels.' + c)"></el-option>
                        </el-select>
                    </div>
                    <div class="col-xl-2 col-md-3 col-6 form-group">
                        <label class="form-control-label">{{ $t("marketing.report.source") }}</label>
                        <el-input v-model="f.source" clearable placeholder="facebook"></el-input>
                    </div>
                    <div class="col-xl-2 col-md-3 col-6 form-group">
                        <label class="form-control-label">{{ $t("marketing.report.medium") }}</label>
                        <el-input v-model="f.medium" clearable placeholder="paid"></el-input>
                    </div>
                    <div class="col-xl-3 col-md-3 form-group">
                        <label class="form-control-label">{{ $t("marketing.report.campaign") }}</label>
                        <el-input v-model="f.campaign" clearable></el-input>
                    </div>
                    <div class="col-xl-3 col-md-3 form-group">
                        <label class="form-control-label">{{ $t("marketing.report.lp") }}</label>
                        <el-select v-model="f.lp_slug" class="w-100" clearable filterable allow-create :placeholder="$t('marketing.lp.tab_all')" @visible-change="loadLps">
                            <el-option v-for="s in lpSlugs" :key="s" :value="s" :label="s"></el-option>
                        </el-select>
                    </div>
                    <div class="col-xl-2 col-md-6 form-group d-flex align-items-end" style="gap: 8px;">
                        <base-button type="primary" size="sm" :loading="loading" data-testid="rep-search" @click="run"><i class="fa fa-search"></i> {{ $t("marketing.report.search") }}</base-button>
                        <base-button type="secondary" size="sm" :loading="exporting" :disabled="!summary" data-testid="rep-export" @click="exportCsv"><i class="fa fa-file-csv"></i> {{ $t("marketing.report.export") }}</base-button>
                    </div>
                </div>
                <small v-if="rangeError" class="text-danger">{{ $t("marketing.report.invalid_range") }}</small>
            </card>

            <el-alert v-if="schemaMissing" type="error" :closable="false" show-icon class="mb-3" :title="$t('marketing.schema_missing')" data-testid="schema-missing"></el-alert>

            <template v-if="summary">
                <!-- cards -->
                <div class="row">
                    <div v-for="c in cardDefs" :key="c.key" class="col-xl-2 col-md-4 col-6">
                        <card class="mb-3 mk-stat" body-classes="py-3">
                            <div class="text-muted small text-uppercase">{{ $t("marketing.report.card." + c.key) }}</div>
                            <div class="h3 mb-0" :data-card="c.key">{{ c.value }}</div>
                        </card>
                    </div>
                </div>

                <!-- chart -->
                <card class="mb-3">
                    <h4 slot="header" class="mb-0">{{ $t("marketing.report.chart_title") }}</h4>
                    <div class="chart" style="height: 280px;">
                        <line-chart v-if="chart.labels.length" :key="chartKey" :height="280" :chart-data="chart" :extra-options="chartOptions"></line-chart>
                        <p v-else class="text-muted">{{ $t("marketing.report.empty") }}</p>
                    </div>
                </card>

                <!-- table -->
                <card class="mb-3" body-classes="px-0 pb-0">
                    <el-table
                        :data="summary.rows"
                        class="bon-table bon-tight mk-rep-table"
                        header-row-class-name="thead-light"
                        show-summary
                        :summary-method="totalsRow"
                        :empty-text="$t('marketing.report.empty')"
                        row-class-name="mk-click-row"
                        data-testid="rep-table"
                        @row-click="openOrders"
                    >
                        <el-table-column v-for="col in keyCols" :key="col" :label="$t('marketing.report.col.' + col)" min-width="130">
                            <template v-slot="{ row }">{{ label(row.key, col) }}</template>
                        </el-table-column>
                        <el-table-column v-for="m in metricCols" :key="m.prop" :label="$t('marketing.report.col.' + m.label)" :min-width="m.money ? 120 : 92" align="right">
                            <template v-slot="{ row }">{{ cell(row, m) }}</template>
                        </el-table-column>
                    </el-table>
                    <div class="px-4 py-2 small text-muted">
                        <span v-if="summary.truncated" class="text-warning mr-2">{{ $t("marketing.report.truncated") }}</span>
                        <span v-if="newMembersNA" class="mr-2">{{ $t("marketing.report.new_members_na") }}</span>
                    </div>
                </card>
            </template>

            <p class="small text-muted" data-testid="rep-footnote">{{ $t("marketing.report.footnote") }}</p>
        </div>

        <!-- the orders of one row. §6.5 says el-drawer; Element here is 2.4.11
             (Drawer arrived in 2.8), so a right-aligned el-dialog plays the part. -->
        <el-dialog :title="drawerTitle" :visible.sync="drawer" width="760px" custom-class="mk-orders-dialog" append-to-body>
            <div data-testid="rep-orders">
                <el-table v-loading="ordersLoading" :data="orders" size="small" class="bon-table" :empty-text="$t('marketing.report.empty')">
                    <el-table-column :label="$t('marketing.report.order_no')" min-width="150">
                        <template v-slot="{ row }">
                            <router-link :to="'/backoffice/orders/' + row.bo_order_id">{{ row.platform_order_id || row.bo_order_id }}</router-link>
                        </template>
                    </el-table-column>
                    <el-table-column :label="$t('marketing.report.ordered_at')" min-width="130"><template v-slot="{ row }">{{ fmt(row.ordered_at) }}</template></el-table-column>
                    <el-table-column :label="$t('marketing.report.status')" prop="order_status" width="80"></el-table-column>
                    <el-table-column :label="$t('marketing.report.amount')" align="right" width="100"><template v-slot="{ row }">{{ money(row.pay_amount) }}</template></el-table-column>
                    <el-table-column :label="$t('marketing.report.channel')" prop="source_channel" width="80"></el-table-column>
                </el-table>
                <el-pagination class="mt-2 text-right" layout="total, prev, pager, next" :total="ordersTotal" :page-size="20" :current-page="ordersPage" @current-change="loadOrders"></el-pagination>
            </div>
        </el-dialog>
    </div>
</template>

<script>
import { DatePicker, Select, Option, Table, TableColumn, Alert, Dialog, Pagination, Loading } from "element-ui";
import LineChart from "@/components/Charts/LineChart";
import { AttributionReportService, LandingPageService } from "@/resource";
import {
    REPORT_GROUPS,
    SOURCE_CHANNELS,
    buildReportQuery,
    defaultReportRange,
    fmtBkk,
    isSchemaMissing,
    keyColumns,
    keyLabel,
    reportCards,
    reportChartData
} from "@/utils/marketing";

const METRICS = [
    { prop: "orders", label: "orders" },
    { prop: "orders_open", label: "open" },
    { prop: "orders_completed", label: "completed" },
    { prop: "orders_cancelled", label: "cancelled" },
    { prop: "orders_returned", label: "returned" },
    { prop: "revenue_placed", label: "revenue_placed", money: true },
    { prop: "revenue_completed", label: "revenue_completed", money: true },
    { prop: "revenue_cancelled_returned", label: "revenue_cancelled_returned", money: true },
    { prop: "aov_placed", label: "aov", money: true },
    { prop: "customers", label: "customers" },
    { prop: "new_members", label: "new_members" }
];

const fmtMoney = (n) => (Number(n) || 0).toLocaleString("th-TH", { minimumFractionDigits: 0, maximumFractionDigits: 2 });

// การตลาด → ที่มาของออเดอร์ / โฆษณา (Phase 1 ads spec 2026-10-13 §6.5 / §3.7).
export default {
    name: "MarketingAttributionReport",
    directives: { loading: Loading.directive },
    components: {
        [DatePicker.name]: DatePicker,
        [Select.name]: Select,
        [Option.name]: Option,
        [Table.name]: Table,
        [TableColumn.name]: TableColumn,
        [Alert.name]: Alert,
        [Dialog.name]: Dialog,
        [Pagination.name]: Pagination, LineChart },
    data() {
        return {
            f: { range: defaultReportRange(), model: "last", group_by: "source_medium", source: "", medium: "", campaign: "", lp_slug: "", source_channel: "" },
            groups: REPORT_GROUPS,
            channels: SOURCE_CHANNELS,
            metricCols: METRICS,
            loading: false,
            exporting: false,
            rangeError: false,
            schemaMissing: false,
            summary: null,
            lastParams: null,
            chart: { labels: [], datasets: [] },
            chartKey: 0,
            chartOptions: { maintainAspectRatio: false, legend: { display: true, position: "bottom" } },
            lpSlugs: [],
            lpsLoaded: false,
            drawer: false,
            drawerKey: null,
            orders: [],
            ordersTotal: 0,
            ordersPage: 1,
            ordersLoading: false
        };
    },
    computed: {
        keyCols() {
            return keyColumns((this.lastParams && this.lastParams.group_by) || this.f.group_by);
        },
        cards() {
            return reportCards(this.summary && this.summary.totals);
        },
        cardDefs() {
            const c = this.cards;
            return [
                { key: "orders", value: c.orders.toLocaleString("th-TH") },
                { key: "revenue_placed", value: fmtMoney(c.revenue_placed) },
                { key: "revenue_completed", value: fmtMoney(c.revenue_completed) },
                { key: "completion", value: c.completion_pct + "%" },
                { key: "aov", value: fmtMoney(c.aov) },
                { key: "new_members", value: this.newMembersHidden || c.new_members === null ? "—" : c.new_members.toLocaleString("th-TH") }
            ];
        },
        // §3.7: new members are not split by channel — with a source_channel
        // filter the API answers null; show "—" rather than a misleading 0.
        newMembersHidden() {
            return !!(this.lastParams && this.lastParams.source_channel);
        },
        newMembersNA() {
            return !!this.summary && !this.newMembersHidden && this.cards.new_members === null;
        },
        drawerTitle() {
            if (!this.drawerKey) return this.$t("marketing.report.drawer_title");
            return this.$t("marketing.report.drawer_title") + " — " + this.keyCols.map((c) => this.label(this.drawerKey, c)).join(" / ");
        }
    },
    created() {
        this.run();
    },
    methods: {
        fmt: fmtBkk,
        money: fmtMoney,
        label: keyLabel,
        cell(row, m) {
            const v = row[m.prop];
            if (m.prop === "new_members" && this.newMembersHidden) return "—";
            if (v === null || v === undefined) return "—";
            return m.money ? fmtMoney(v) : Number(v).toLocaleString("th-TH");
        },
        totalsRow({ columns }) {
            const t = (this.summary && this.summary.totals) || {};
            const nKey = this.keyCols.length;
            return columns.map((col, i) => {
                if (i === 0) return this.$t("marketing.report.total");
                if (i < nKey) return "";
                const m = METRICS[i - nKey];
                return m ? this.cell(t, m) : "";
            });
        },
        async run() {
            const q = buildReportQuery(this.f);
            this.rangeError = !q.ok;
            if (!q.ok) return;
            this.loading = true;
            this.lastParams = q.params;
            try {
                const [s, ts] = await Promise.all([
                    AttributionReportService.summary(q.params),
                    AttributionReportService.timeseries(q.params).catch(() => null)
                ]);
                this.summary = (s.data && s.data.data) || { rows: [], totals: {} };
                this.chart = reportChartData(ts && ts.data && ts.data.data, this.$t("marketing.report.other"));
                this.chartKey += 1;
                this.schemaMissing = false;
            } catch (err) {
                this.summary = null;
                if (isSchemaMissing(err)) this.schemaMissing = true;
                else if (err && err.response && err.response.status === 400) this.rangeError = true;
                else this.$notify({ type: "danger", message: this.$t("marketing.report.load_error") });
            } finally {
                this.loading = false;
            }
        },
        async loadLps(open) {
            if (!open || this.lpsLoaded) return;
            this.lpsLoaded = true;
            try {
                const res = await LandingPageService.list({ page: 1, limit: 100 });
                this.lpSlugs = (((res.data && res.data.data) || []).map((r) => r.slug)).filter(Boolean);
            } catch (e) {
                this.lpsLoaded = false;
            }
        },
        // A row's key → the exact-match filters for /orders ("(direct)" = empty).
        openOrders(row) {
            this.drawerKey = row.key || {};
            this.ordersPage = 1;
            this.drawer = true;
            this.loadOrders(1);
        },
        async loadOrders(page) {
            this.ordersPage = page;
            this.ordersLoading = true;
            const filters = {};
            this.keyCols.forEach((c) => {
                const v = this.drawerKey[c];
                filters[c] = v === undefined || v === null || v === "" ? "(direct)" : v;
            });
            if (filters.term !== undefined) delete filters.term; // no term filter in §3.7
            const q = buildReportQuery({ ...this.f, ...this.lastParams, range: [this.lastParams.from, this.lastParams.to], ...filters, page, limit: 20 });
            try {
                const res = await AttributionReportService.orders(q.params);
                this.orders = (res.data && res.data.data) || [];
                this.ordersTotal = (res.data && res.data.total) || 0;
            } catch (e) {
                this.orders = [];
                this.ordersTotal = 0;
            } finally {
                this.ordersLoading = false;
            }
        },
        async exportCsv() {
            if (!this.lastParams) return;
            this.exporting = true;
            try {
                const res = await AttributionReportService.exportCsv(this.lastParams);
                const blob = res.data instanceof Blob ? res.data : new Blob([res.data], { type: "text/csv;charset=utf-8" });
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = `order-sources-${this.lastParams.from}_${this.lastParams.to}.csv`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                setTimeout(() => URL.revokeObjectURL(url), 1000);
            } catch (err) {
                this.$notify({ type: "danger", message: isSchemaMissing(err) ? this.$t("marketing.schema_missing") : this.$t("marketing.report.load_error") });
            } finally {
                this.exporting = false;
            }
        }
    }
};
</script>

<style scoped>
.mk-stat .h3 {
    white-space: nowrap;
}
.mk-report >>> .mk-click-row {
    cursor: pointer;
}
</style>
